"use client";

import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";
import type { ComponentProps, ReactNode, Ref } from "react";

import { EmptyState } from "@/components/ui/empty-state";
import {
  TABLE_PINNED_COLUMN,
  TABLE_ROW_BACKGROUND,
  TABLE_ROW_HOVER_BACKGROUND,
  TABLE_ROW_STRIPE_BACKGROUND,
  TABLE_STICKY_HEADER,
  TableScrollContainer,
} from "@/components/ui/table-grid";
import { cn } from "@/lib/utils";
import { columnWidthStyle, fluidColumnWidthStyle } from "./record-table-state";

/*
 * The record grid as markup. RecordTableGrid keeps the live machinery (the
 * TanStack table, virtualization, focus, keyboard navigation and load-more)
 * and renders through these, so fixture records compose the same grid at
 * rest: each row `index * ROW_HEIGHT` down the body, each column at its
 * width. A client module: the row fills and column widths it reads come from
 * client modules, so a Server Component renders these with plain props.
 */

/** The pinned actions column keeps its width; every other column grows to fill the grid. */
function columnStyle(width: number, pinned: boolean) {
  return pinned ? columnWidthStyle(width) : fluidColumnWidthStyle(width);
}

/** The grid's frame: its scrolling surface, the table at its columns' width, the sticky header and the body at its rows' height. */
export function RecordTableGridView({
  scrollRef,
  caption,
  tableWidth,
  bodyHeight,
  header,
  footer,
  emptyMessage = null,
  loader,
  children,
}: {
  scrollRef?: Ref<HTMLDivElement>;
  /** Names the table for assistive technology. */
  caption?: string;
  /** The columns' total width, the table's minimum. */
  tableWidth: number;
  /** The rows' total height; a static grid's is its row count times ROW_HEIGHT. */
  bodyHeight: number;
  /** The header rows. */
  header?: ReactNode;
  /** Inside the table after the body: the new-record row in the application. */
  footer?: ReactNode;
  /** Shown under the table when there are no records. */
  emptyMessage?: string | null;
  /** Under the table: the load-more control in the application. */
  loader?: ReactNode;
  /** The body rows. */
  children?: ReactNode;
}) {
  return (
    <TableScrollContainer
      ref={scrollRef}
      className="mlx-floating-panel-scroll-surface"
    >
      <table
        role="grid"
        className="grid w-full border-separate border-spacing-0 text-sm"
        style={{ minWidth: tableWidth }}
      >
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead className={cn(TABLE_STICKY_HEADER, "z-10")}>{header}</thead>
        <tbody
          className="relative grid"
          style={{ height: `${bodyHeight}px` }}
        >
          {children}
        </tbody>
        {footer}
      </table>
      {emptyMessage !== null ? <EmptyState>{emptyMessage}</EmptyState> : null}
      {loader}
    </TableScrollContainer>
  );
}

/** A header row of the grid. */
export function RecordTableHeaderRowView({
  children,
}: {
  children?: ReactNode;
}) {
  return <tr className="flex w-full text-left">{children}</tr>;
}

/** A column header: its label, or a button that sorts by the column with the direction's arrow, then its resize handle. */
export function RecordTableHeaderCellView({
  width,
  pinned = false,
  label,
  name = "column",
  sortable = false,
  sorted = null,
  onSort,
  resizeHandle,
}: {
  /** The column's width in pixels. */
  width: number;
  /** The actions column: pinned to the start at a fixed width, its content centred. */
  pinned?: boolean;
  label?: ReactNode;
  /** The column's name as text, for its sort button's label. */
  name?: string;
  /** The header is a button that sorts by its column. */
  sortable?: boolean;
  /** The direction, when the grid is sorted by this column. */
  sorted?: "asc" | "desc" | null;
  onSort?: () => void;
  resizeHandle?: ReactNode;
}) {
  return (
    <th
      aria-sort={
        sorted ? (sorted === "asc" ? "ascending" : "descending") : undefined
      }
      className={cn(
        "relative flex h-7 items-center border-b border-r border-border/40 px-2 py-0 whitespace-nowrap text-xs font-medium tracking-normal text-muted-foreground last:border-r-0",
        pinned
          ? `${TABLE_PINNED_COLUMN} z-30 justify-center bg-background px-0`
          : "",
      )}
      style={columnStyle(width, pinned)}
    >
      {sortable ? (
        <button
          type="button"
          className={cn(
            "flex min-w-0 flex-1 items-center gap-1 rounded-sm text-left hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            sorted ? "text-foreground" : null,
          )}
          aria-label={`Sort by ${name}`}
          onClick={onSort}
        >
          <span className="truncate">{label}</span>
          {sorted === "asc" ? (
            <ArrowUpIcon className="size-3 shrink-0" aria-hidden />
          ) : sorted === "desc" ? (
            <ArrowDownIcon className="size-3 shrink-0" aria-hidden />
          ) : null}
        </button>
      ) : (
        <span className="truncate">{label}</span>
      )}
      {resizeHandle}
    </th>
  );
}

/** A body row, `start` pixels down the body: even rows are striped, and the row holding the active cell is highlighted. */
export function RecordTableRowView({
  index,
  start,
  tableWidth,
  selected = false,
  ref,
  children,
}: {
  /** The row's position among the records, which sets its stripe. */
  index: number;
  /** The row's offset down the body; a static grid's is `index * ROW_HEIGHT`. */
  start: number;
  /** The columns' total width, the row's minimum. */
  tableWidth: number;
  /** The row holds the active cell. */
  selected?: boolean;
  ref?: Ref<HTMLTableRowElement>;
  children?: ReactNode;
}) {
  return (
    <tr
      data-index={index}
      data-selected={selected}
      ref={ref}
      className={cn(
        "group/row absolute flex h-8",
        index % 2 === 0 ? TABLE_ROW_STRIPE_BACKGROUND : TABLE_ROW_BACKGROUND,
        TABLE_ROW_HOVER_BACKGROUND,
        "[&[data-selected=true]>td]:[background-color:color-mix(in_oklab,hsl(var(--muted))_72%,hsl(var(--surface)))]",
      )}
      style={{
        transform: `translateY(${start}px)`,
        width: "100%",
        minWidth: tableWidth,
      }}
    >
      {children}
    </tr>
  );
}

/**
 * A body cell at its column's width, its content in a clipping wrapper. The
 * grid's own attributes and handlers (role, tabIndex, aria-selected, the
 * active-cell marker, focus and keyboard handling, the ref) pass through.
 */
export function RecordTableCellView({
  width,
  pinned = false,
  active = false,
  editing = false,
  children,
  ...cellProps
}: {
  /** The column's width in pixels. */
  width: number;
  /** The actions column: pinned to the start at a fixed width, its content centred. */
  pinned?: boolean;
  /** The grid's active cell, outlined. */
  active?: boolean;
  /** An open editor, which may overflow the cell. */
  editing?: boolean;
  children?: ReactNode;
} & Omit<ComponentProps<"td">, "className" | "style" | "children">) {
  return (
    <td
      {...cellProps}
      className={cn(
        "flex max-w-0 items-center border-r border-b border-border/40 px-2 py-0.5 align-middle last:border-r-0",
        // The pinned cell inherits its row's opaque [&>td] fill, so
        // it matches the row exactly and needs no per-cell override.
        pinned ? cn(TABLE_PINNED_COLUMN, "justify-center px-0") : "",
        active && "relative z-10 outline-2 -outline-offset-2 outline-ring",
      )}
      style={columnStyle(width, pinned)}
    >
      <div
        className={cn(
          "w-full min-w-0",
          !editing && !pinned && "overflow-hidden whitespace-nowrap",
          pinned ? "flex justify-center" : "flex-1",
        )}
      >
        {children}
      </div>
    </td>
  );
}
